import { alpha } from '@mui/material/styles'
import { ReactComponent as CaretDown } from '../../../assets/icons/caret-down.svg'
import { ReactComponent as X } from '../../../assets/icons/x.svg'

import theme from '../base'

const form = {
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
      margin: 'none',
    },
    styleOverrides: {
      root: {
        '& input:read-only': {
          borderColor: alpha(theme.palette.neutral.lighter, 0.4),
          backgroundColor: theme.palette.neutral.lightest,
          color: theme.palette.neutral.main,
          pointerEvents: 'none',
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        '&.Mui-error .MuiInput-input': {
          boxShadow: `0 0 0 100px ${theme.palette.error.light} inset`,
          backgroundColor: theme.palette.error.light,
          borderColor: theme.palette.primary.lightest,
          borderBottomColor: theme.palette.error.dark,
        },

        '&:hover .MuiSelect-icon': {
          color: theme.palette.primary.main,
        },

        '&.Mui-disabled': {
          color: theme.palette.neutral.dark,
          pointerEvents: 'none',
          opacity: 0.5,
        },

        '& .MuiIconButton-root:focus-visible': {
          backgroundColor: theme.palette.neutral.lightest,
        },
      },
      input: {
        height: theme.spacing(5),
        padding: theme.spacing(0, 2.25),
        background: theme.palette.neutral.white,
        ...theme.typography.inputText,
        color: theme.palette.neutral.dark,
        border: `1px solid ${alpha(theme.palette.neutral.lighter, 0.4)}`,
        boxSizing: 'border-box',
        transition: theme.transitions.create('box-shadow', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),

        '&::placeholder': {
          color: theme.palette.neutral.light,
          opacity: 1,
        },

        '&:hover': {
          boxShadow: theme.shadows[1],
        },

        '&:focus, &:-webkit-autofill:focus': {
          backgroundColor: theme.palette.neutral.lightest,
        },

        '&:-webkit-autofill, &:-webkit-autofill:hover': {
          boxShadow: `0 0 0 100px ${theme.palette.neutral.white} inset`,
        },
      },
      inputMultiline: {
        padding: theme.spacing(1, 2),
      },
      adornedEnd: {
        display: 'inline-block',
        position: 'relative',

        '& .MuiInputBase-input': {
          paddingRight: theme.spacing(7),
        },

        '& .MuiInputAdornment-root': {
          position: 'absolute',
          right: theme.spacing(2),
          top: '50%',
        },

        '& .MuiButtonBase-root': {
          color: theme.palette.neutral.light,
        },
      },
    },
  },
  MuiInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      input: {
        // TODO: set read only select styles. pseudo-class 'not' is necesary because
        // all selects are read-only by default in MaterialUI
        '&:read-only:not(.Mui-disabled):not(.MuiSelect-select)': {
          pointerEvents: 'none',
        },

        '&.Mui-disabled:not(.MuiSelect-root)': {
          borderColor: theme.palette.neutral.lighter,
        },
      },
      formControl: {
        'label + &': {
          marginTop: theme.spacing(3.25),
        },
      },
      root: {
        '&.multiLineSelect': {
          display: 'block',
          width: `calc(100% - ${theme.spacing(4)})`,
          margin: '0 auto',

          '&.is-fullWidth': { width: '100%' },

          '& .MuiSelect-select': { height: 80 },
          '& .multiLineSelect-menuItem': {
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
          },
          '& .multiLineSelect-clientCenterName': {
            display: 'block',
            color: theme.palette.neutral.main,
            marginBottom: theme.spacing(0.5),
            width: `calc(100vw - ${theme.spacing(8)})`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .multiLineSelect-clientCenterId': {
            display: 'block',
            color: theme.palette.neutral.light,
          },
        },
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'standard',
      IconComponent: CaretDown,
      MenuProps: {
        elevation: 1,
      },
      displayEmpty: true,
    },
    styleOverrides: {
      select: {
        height: theme.spacing(5),
        display: 'flex',
        alignItems: 'center',
        '&.Mui-disabled': {
          borderColor: theme.palette.neutral.lighest,
        },
        '&.MuiSelect-select': {
          padding: theme.spacing(0, 5, 0, 2),
        },
        '& em': {
          // Placeholder
          color: theme.palette.neutral.light,
          fontStyle: 'normal',
        },
      },
      icon: {
        height: theme.spacing(3),
        width: theme.spacing(3),
        top: `calc(50% - ${theme.spacing(1.5)})`,
        right: theme.spacing(1),
        color: theme.palette.neutral.light,
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: <CaretDown />,
      clearIcon: <X />,
      forcePopupIcon: true,
    },
    styleOverrides: {
      root: {
        // TODO: hack because specificity issues
        '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
          {
            paddingRight: 0,
          },

        '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-of-type, & .MuiAutocomplete-inputRoot[class*="MuiInput-root"][class*="MuiInput-marginDense"] .MuiAutocomplete-input:first-of-type':
          {
            width: '100%',
            height: theme.spacing(5),
            padding: theme.spacing(0, 10, 0, 2),
          },

        '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-of-type.inputGPlacesAutocomplete':
          {
            padding: theme.spacing(0, 4.5, 0, 2),
          },

        '&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot, &.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
          {
            paddingRight: '0',
          },

        '& .MuiCircularProgress-root': {
          position: 'absolute',
          top: theme.spacing(1.1),
          right: theme.spacing(5),
          color: theme.palette.neutral.light,
        },

        '&.searchAddress': {
          '& .MuiAutocomplete-endAdornment button:only-child': {
            top: 2,
          },
          '& .MuiAutocomplete-popupIndicator': {
            transform: 'rotate(0deg)',
          },
        },
      },
      endAdornment: {
        top: `calc(50% - ${theme.spacing(2)})`,
        right: theme.spacing(1),

        '& svg': {
          width: theme.spacing(3),
          height: theme.spacing(3),
        },
      },
      noOptions: {
        ...theme.typography.inputText,
      },
      clearIndicatorDirty: {
        marginRight: theme.spacing(0.5),
      },
      listbox: {
        '& .MuiAutocomplete-option[aria-selected="true"]': {
          backgroundColor: 'inherit',
          color: theme.palette.primary.main,
          '&.Mui-focused': {
            backgroundColor: theme.palette.neutral.white,
          },
        },
      },
      option: {
        height: theme.spacing(5),
        padding: theme.spacing(0, 2),
        ...theme.typography.inputText,
        color: theme.palette.neutral.main,

        '&:hover, &.Mui-selected:hover': {
          backgroundColor: theme.palette.neutral.lightest,
        },
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        width: '100%',
        pointerEvents: 'auto',
        [theme.breakpoints.down('sm')]: {
          marginBottom: theme.spacing(1),
        },

        '& .MuiInputLabel-asterisk, & .MuiFormLabel-asterisk': {
          display: 'inline-block',
          position: 'absolute',
          top: '0.2188rem',
          width: theme.spacing(1),
          height: theme.spacing(1),
          backgroundColor: theme.palette.primary.lightest,
          color: theme.palette.primary.main,
          borderRadius: '50%',
          marginLeft: theme.spacing(0.5),
          fontSize: 14,
          lineHeight: '0.9063rem',
          letterSpacing: -1.5,
        },
      },
      shrink: {
        transform: 'scale(1)',
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        ...theme.typography.label,
        color: theme.palette.neutral.main,

        '& .MuiTypography-h3, & .MuiTypography-h4bold, &.Mui-focused': {
          color: theme.palette.neutral.dark,
        },

        '&.Mui-disabled': {
          color: theme.palette.neutral.main,
          opacity: 0.5,
          pointerEvents: 'none',

          // Tooltip
          '& svg': {
            color: theme.palette.neutral.main,
          },
        },

        '&.Mui-error': {
          color: theme.palette.neutral.main,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: 0,
        padding: theme.spacing(0.5, 0, 0, 0),
        ...theme.typography.label,
        color: theme.palette.neutral.main,

        '&.Mui-error': {
          color: theme.palette.error.dark,
        },

        '&.Mui-disabled': {
          color: theme.palette.neutral.light,
          opacity: 0.5,
        },

        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(1, 1, 0, 2),
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        ...theme.typography.label,
        color: theme.palette.neutral.main,
        width: '100%',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        width: 'inherit',
        // SIZES
        '&.input-microXS, & .input-microXS': {
          width: 80,
        },
        '&.input-micro, & .input-micro': {
          width: 118,
        },
        '&.input-smaller, & .input-smaller': {
          width: 167,
        },
        '&.input-small, & .input-small': {
          width: 200,
        },
        '&.input-medium, & .input-medium': {
          width: 280,
        },
        '&.input-large, & .input-large': {
          width: 360,
        },
        '&.input-extra-large, & .input-extra-large': {
          width: 565,
        },
        '&.input-full, & .input-full': {
          width: '100%',
        },
        '&.label-heading': {
          '& label + .MuiInput-formControl': {
            marginTop: theme.spacing(4.25),
          },
        },
        '& textarea': {
          padding: theme.spacing(1, 2),
          backgroundColor: theme.palette.neutral.white,
          borderColor: alpha(theme.palette.neutral.lighter, 0.4),
          fontFamily: 'ActoMedium',
          fontSize: 16,
          borderRadius: 0,
          maxWidth: 630,
          resize: 'none',
          '&::placeholder': {
            opacity: 0.5,
            color: theme.palette.neutral.dark,
          },
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        '&.select-menu, &.MuiAutocomplete-paper': {
          marginTop: theme.spacing(0.5),

          [theme.breakpoints.down('sm')]: {
            marginTop: 0,
          },

          '& .MuiList-root, & .MuiAutocomplete-listbox': {
            padding: 0,
          },

          '& .MuiList-root, & .MuiMenu-list': {
            maxHeight: 200,
            overflowY: 'auto',
          },

          '& .MuiMenuItem-root, & .MuiAutocomplete-option': {
            height: theme.spacing(5),
            padding: theme.spacing(0, 2),
            ...theme.typography.inputText,
            color: theme.palette.neutral.main,
            whiteSpace: 'inherit',

            '&:hover, &.Mui-selected:hover': {
              backgroundColor: theme.palette.neutral.lightest,
            },

            '&.Mui-selected': {
              backgroundColor: 'inherit',
              color: theme.palette.primary.main,
            },

            '& em': {
              // Placeholder
              color: theme.palette.neutral.light,
            },

            '&.hidden': {
              display: 'none',
            },
          },
        },
        '&.select-menuMultiline': {
          left: `${theme.spacing(2)} !important`,
          minWidth: `calc(100% - ${theme.spacing(4)}) !important`,

          '&.is-fullWidth': {
            [theme.breakpoints.down('sm')]: {
              left: '0 !important',
              minWidth: '100% !important',
            },
          },

          '& .multiLineSelect-menuItem': {
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height: 60,
          },
          '& .multiLineSelect-clientCenterName': {
            display: 'block',
            marginBottom: theme.spacing(0.5),
            width: `calc(100vw - ${theme.spacing(4)})`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: theme.palette.neutral.main,
          },
          '& .multiLineSelect-clientCenterId': {
            display: 'block',
            color: theme.palette.neutral.light,
          },
          '& .multiLineSelect-menuItem.Mui-selected': {
            '& .multiLineSelect-clientCenterName': {
              color: theme.palette.primary.main,
            },
            '& .multiLineSelect-clientCenterId': {
              color: theme.palette.primary.light,
            },
          },
        },
        [theme.breakpoints.down('sm')]: {
          borderRadius: 0,
        },
      },
      elevation8: {
        boxShadow: theme.shadows[2],
      },
    },
  },
  MuiPickersBasePicker: {
    styleOverrides: {
      switchHeader: {
        '& .MuiTypography-root': {
          ...theme.typography.body1,
          position: 'relative',
          margin: '0 auto',
        },
      },
    },
  },
  MuiCalendarPicker: {
    styleOverrides: {
      root: {
        '& > *': {
          fontFamily: 'ActoMedium',
          textTransform: 'capitalize',
        },
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      transitionContainer: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
      },
      dayLabel: {
        textTransform: 'capitalize',
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        width: theme.spacing(4.5),
        height: theme.spacing(4.5),
        margin: theme.spacing(0, 0),
        '&:not(.Mui-selected)': {
          border: 'none',
          '&:hover': {
            border: 'none',
          },
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.neutral.white,
          '&:focus': {
            backgroundColor: theme.palette.primary.main,
          },
          '&:hover': {
            color: theme.palette.neutral.white,
            backgroundColor: theme.palette.primary.dark,
          },
        },
        '&:hover': {
          color: theme.palette.primary.main,
          backgroundColor: 'transparent',
        },
      },
      daySelected: {
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.neutral.white,
        },
      },
      dayDisabled: {
        color: theme.palette.neutral.light,
      },
    },
  },
  MuiDateRangePickerDay: {
    styleOverrides: {
      root: {
        '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
          backgroundColor: alpha(theme.palette.primary.lightest, 0.5),
          '& .MuiButtonBase-root:not(.Mui-selected)': {
            color: theme.palette.neutral.dark,
          },
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        width: 38,
        height: 38,
        '&:hover': {
          color: theme.palette.neutral.lightest,
        },
      },
    },
  },
}

export default form
